import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const CancellationRefunds = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ background: '#F9FAFB' }}>
      <header className="bg-white shadow-sm border-b" style={{ borderColor: '#E5E7EB' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition">
            <ArrowLeft size={20} />
            Back to Home
          </button>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#002147' }}>Cancellation and Refunds Policy</h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <p className="text-sm text-gray-600 mb-8">Last Updated: {new Date().toLocaleDateString()}</p>

          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-8">
            <p className="text-sm text-gray-700">
              <strong>Important:</strong> The ₹200 application fee is non-refundable. Please read this policy carefully before making payment.
            </p>
          </div>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>1. Non-Refundable Application Fee</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              The application fee of ₹200 is <strong>non-refundable</strong> under all circumstances, including but not limited to:
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li>Application rejection or non-selection</li>
              <li>Withdrawal of application by parent/guardian</li>
              <li>Change of mind after payment</li>
              <li>Duplicate or accidental payments</li>
              <li>Incomplete application submission</li>
              <li>Non-attendance at scheduled interview</li>
              <li>Technical issues after payment completion</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              The application fee covers administrative costs of processing the application, document verification, interview scheduling, and system maintenance.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>2. Payment Processing</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Payment Gateway:</strong>
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li>All payments are processed through Razorpay, a secure and RBI-approved payment gateway</li>
              <li>Razorpay supports credit cards, debit cards, net banking, UPI, and wallets</li>
              <li>Payment confirmation is instant</li>
              <li>Transaction details are emailed to your registered email address</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Payment Security:</strong>
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li>All transactions are encrypted using 256-bit SSL</li>
              <li>Card details are not stored on our servers</li>
              <li>PCI-DSS compliant payment processing</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>3. Exceptional Circumstances</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Refunds may be considered ONLY in the following exceptional cases:
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li><strong>Payment Deducted but Service Not Provided:</strong> If payment is successful but login credentials are not generated within 24 hours</li>
              <li><strong>Technical Error:</strong> If payment is deducted multiple times due to system error</li>
              <li><strong>Portal Shutdown:</strong> If the admission portal is closed before application submission</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Refund Process for Exceptional Cases:</strong>
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li>Contact us through the Contact Us page with transaction details</li>
              <li>Provide payment proof (transaction ID, screenshot, receipt)</li>
              <li>Explain the issue clearly</li>
              <li>Our team will investigate within 5-7 business days</li>
              <li>If approved, refund will be processed within 10-15 business days</li>
              <li>Refund will be credited to the original payment method</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>4. Failed Transactions</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Payment Failed but Amount Deducted:</strong>
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li>If payment fails but amount is debited from your account, it will be automatically refunded by your bank within 5-7 business days</li>
              <li>This is a standard banking process and does not require our intervention</li>
              <li>Check with your bank if amount is not refunded within 7 days</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Transaction Pending:</strong>
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li>If transaction status shows "Pending", wait for 30 minutes</li>
              <li>Most pending transactions are auto-resolved</li>
              <li>If still pending after 1 hour, contact our support team</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>5. Application Cancellation</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Voluntary Withdrawal:</strong>
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li>Parents can withdraw their application at any time</li>
              <li>To cancel: Contact us through Contact Us page or email</li>
              <li>Cancellation will be processed within 2 business days</li>
              <li>Application fee will NOT be refunded upon withdrawal</li>
              <li>Access to the portal will be revoked after cancellation</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>School-Initiated Cancellation:</strong>
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li>School may cancel application for reasons including fake documents, incomplete information, or policy violations</li>
              <li>Parents will be notified via email</li>
              <li>Application fee is non-refundable even in school-initiated cancellations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>6. Duplicate Payments</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Accidental Multiple Payments:</strong>
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li>If you accidentally make multiple payments for the same application, contact us immediately</li>
              <li>Provide both transaction IDs</li>
              <li>We will verify the duplicate payment</li>
              <li>One application fee will be retained, others will be refunded</li>
              <li>Refund processed within 10-15 business days</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>7. Refund Processing Time</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Approved Refund Timeline:</strong>
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li><strong>Investigation:</strong> 5-7 business days</li>
              <li><strong>Approval:</strong> 2-3 business days</li>
              <li><strong>Refund Initiation:</strong> 1-2 business days</li>
              <li><strong>Bank Credit:</strong> 5-10 business days (varies by bank)</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Total Processing Time:</strong> 15-20 business days from refund request to bank credit
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>8. Refund Method</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Refunds will be processed using the same payment method:
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li><strong>Credit Card:</strong> Refunded to the same credit card</li>
              <li><strong>Debit Card:</strong> Refunded to the same debit card</li>
              <li><strong>Net Banking:</strong> Refunded to the source bank account</li>
              <li><strong>UPI:</strong> Refunded to the UPI ID used</li>
              <li><strong>Wallet:</strong> Refunded to the same wallet</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              We cannot process refunds to a different payment method or bank account.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>9. How to Request a Refund</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Step-by-Step Process:</strong>
            </p>
            <ol className="list-decimal ml-6 text-gray-700 mb-4">
              <li>Visit our Contact Us page</li>
              <li>Send email to: info@betinstitutions.org</li>
              <li>Subject: "Refund Request - [Transaction ID]"</li>
              <li>Include:
                <ul className="list-disc ml-6 mt-2">
                  <li>Full name</li>
                  <li>Registered email and phone number</li>
                  <li>Transaction ID / Payment reference number</li>
                  <li>Payment date and amount</li>
                  <li>Reason for refund request</li>
                  <li>Payment screenshot/receipt</li>
                </ul>
              </li>
              <li>Wait for our team's response (within 48 hours)</li>
            </ol>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>10. Chargebacks and Disputes</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Bank Chargebacks:</strong>
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li>We discourage filing chargebacks without contacting us first</li>
              <li>Chargebacks may result in account suspension and legal action</li>
              <li>Contact us first to resolve payment disputes</li>
              <li>We commit to fair and prompt resolution</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Dispute Resolution:</strong>
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li>All disputes will be handled fairly and transparently</li>
              <li>Our team will investigate thoroughly</li>
              <li>Decision will be communicated via email</li>
              <li>Decisions are based on evidence and policy</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>11. GST and Taxes</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <ul className="list-disc ml-6 text-gray-700 mb-4">
                <li>Application fee of ₹200 is inclusive of all taxes</li>
                <li>No additional GST or charges will be levied</li>
                <li>Invoice/receipt available on request</li>
              </ul>
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>12. Contact for Refund Queries</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              For any refund-related queries:
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li><strong>Email:</strong> info@betinstitutions.org</li>
              <li><strong>Subject Line:</strong> "Refund Query - [Your Name]"</li>
              <li><strong>Response Time:</strong> Within 48 hours</li>
              <li><strong>Contact Us Page:</strong> Available on our website</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>13. Policy Updates</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              This Cancellation and Refunds Policy may be updated periodically. Users will be notified of significant changes via email. The "Last Updated" date at the top indicates the most recent revision. Continued use of the portal after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mt-8">
            <p className="text-sm text-gray-700">
              <strong>Reminder:</strong> By proceeding with payment, you acknowledge that you have read, understood, and agree to this Cancellation and Refunds Policy, including the non-refundable nature of the ₹200 application fee.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancellationRefunds;
